import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaArrowRight, FaQrcode, FaShieldAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const UPIPay = () => {
  const [amount, setAmount] = useState('500');
  const [qrData, setQrData] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const UpiID = "teleroidgroup@axl";
  const RecipientName = "Abhishek";

  // Sponsorship Presets
  const presets = [
    { label: "Coffee & Server", value: "300" },
    { label: "Bugfix Fuel", value: "500" },
    { label: "Feature Backer", value: "1500" },
    { label: "Core Sponsor", value: "3000" },
  ];

  // Popular UPI apps logos
  const upiApps = [
    { name: "Google Pay", logo: "https://i.ibb.co/Wp3rvsLK/Google-Pay-Logo.png" },
    { name: "PhonePe", logo: "https://i.ibb.co/MyhvH3XC/phonepe.webp" },
    { name: "BHIM", logo: "https://i.ibb.co/gbNP1F0G/bhim.png" },
    { name: "Paytm", logo: "https://i.ibb.co/nNcQCXnw/bharatpe.png" },
    { name: "Amazon Pay", logo: "https://i.ibb.co/KxWgjNr6/amazon.png" },
  ];

  useEffect(() => {
    if (amount) {
      setIsGenerating(true);
      const sanitizedAmount = parseFloat(amount);
      
      const timer = setTimeout(() => {
        if (!isNaN(sanitizedAmount) && sanitizedAmount > 0) {
          const upiURL = `upi://pay?pa=${UpiID}&am=${sanitizedAmount}&pn=${RecipientName}&cu=INR&tn=Developer%20Project%20Sponsorship`;
          setQrData(upiURL);
        } else {
          setQrData('');
        }
        setIsGenerating(false);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setQrData('');
    }
  }, [amount]);

  const handlePayThroughUPI = () => {
    if (qrData) {
      window.location.href = qrData;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-xl mx-auto">
        {/* Breadcrumb / Nav */}
        <div className="mb-6 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 transition"
          >
            ← Back to Sponsorship Hub
          </Link>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center gap-1.5">
            <FaShieldAlt className="text-emerald-600" />
            0% Platform Fee
          </span>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white p-5 sm:p-9 rounded-3xl shadow-xs border border-slate-200/80"
        >
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center text-xl mx-auto mb-3">
              <FaRupeeSign />
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Instant UPI Sponsorship
            </h1>
            <p className="mt-1.5 text-sm sm:text-base text-slate-600 font-medium">
              Sponsor developer <strong className="text-slate-900 font-semibold">{RecipientName}</strong> directly with zero middleman deductions
            </p>
          </div>

          {/* Quick Preset Buttons */}
          <div className="mb-6">
            <label className="block text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-600 mb-2.5">
              Select Sponsorship Amount
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {presets.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setAmount(preset.value)}
                  className={`py-2.5 px-3 rounded-2xl text-sm font-bold transition border cursor-pointer ${
                    amount === preset.value
                      ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                      : "bg-[#FAF9F6] text-slate-700 border-slate-200 hover:border-slate-300 active:bg-slate-100"
                  }`}
                >
                  <div className="text-sm font-extrabold">₹{preset.value}</div>
                  <div className="text-[11px] font-medium opacity-80 mt-0.5">{preset.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount Input */}
          <div className="mb-6">
            <label className="block text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-600 mb-2">
              Or Custom Contribution (₹)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 font-bold">
                <FaRupeeSign />
              </div>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="block w-full pl-10 pr-4 py-3.5 bg-[#FAF9F6] border border-slate-200 rounded-2xl text-slate-900 font-extrabold text-base sm:text-lg placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition duration-200"
                placeholder="500"
                min="1"
                step="1"
              />
            </div>
          </div>

          {/* QR Code Section */}
          {isGenerating ? (
            <div className="flex justify-center items-center h-64 bg-[#FAF9F6] rounded-2xl border border-slate-200">
              <div className="animate-pulse flex flex-col items-center">
                <div className="w-44 h-44 bg-slate-200 rounded-xl mb-3"></div>
                <p className="text-xs text-slate-500 font-medium">Generating secure dynamic QR...</p>
              </div>
            </div>
          ) : qrData ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col items-center"
            >
              <div className="bg-[#FAF9F6] p-4 sm:p-5 rounded-2xl border border-slate-200/90 shadow-xs mb-5 relative w-full flex flex-col items-center">
                <div className="relative bg-white p-3 rounded-xl border border-slate-200 shadow-xs">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}&bgcolor=ffffff&color=0f172a&qzone=1`}
                    alt="UPI Payment QR Code"
                    className="w-40 h-40 sm:w-48 sm:h-48 rounded-lg"
                  />
                  <div className="absolute -top-3 -right-3 bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-md">
                    <FaQrcode size={14} />
                  </div>
                </div>

                <div className="mt-3 text-center">
                  <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-slate-500">Scan via any UPI App</p>
                  <p className="text-2xl sm:text-3xl font-black text-slate-900 font-display mt-0.5">₹{amount || "0"}</p>
                </div>
              </div>

              {/* Mobile Direct Pay Button */}
              <button
                onClick={handlePayThroughUPI}
                className="w-full bg-slate-900 hover:bg-slate-800 active:scale-98 text-white font-bold py-3.5 px-6 rounded-2xl shadow-xs hover:shadow transition duration-200 flex items-center justify-center gap-2 cursor-pointer text-sm sm:text-base"
              >
                <span>Open in Mobile UPI App</span>
                <FaArrowRight className="text-xs" />
              </button>

              {/* Supported Apps List */}
              <div className="mt-6 w-full pt-6 border-t border-slate-100">
                <p className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Instant Support on All UPI Apps
                </p>
                <div className="flex justify-center items-center gap-5">
                  {upiApps.map((app, index) => (
                    <div 
                      key={index}
                      className="opacity-70 hover:opacity-100 transition" 
                      title={app.name}
                    >
                      <img 
                        src={app.logo} 
                        alt={app.name} 
                        className="h-7 object-contain" 
                      />
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="flex flex-col items-center justify-center h-60 bg-[#FAF9F6] rounded-2xl border-2 border-dashed border-slate-200">
              <div className="text-center p-6">
                <div className="mx-auto w-12 h-12 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center mb-3">
                  <FaRupeeSign className="text-slate-400" />
                </div>
                <h3 className="text-sm font-bold text-slate-700">Enter a Sponsorship Amount</h3>
                <p className="text-xs text-slate-400 mt-1">Dynamic payment QR will generate automatically</p>
              </div>
            </div>
          )}

          {/* Footer Info */}
          <div className="mt-8 text-center border-t border-slate-100 pt-5">
            <p className="text-xs font-medium text-slate-500">
              Verified UPI ID: <code className="bg-slate-100 px-2.5 py-1 rounded-lg text-slate-800 font-mono font-semibold">{UpiID}</code>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default UPIPay;
